import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { sessaoAtiva } from '@/nucleo/auth/armazem-token';
import { chamarApi, ErroApi } from '@/nucleo/auth/cliente-api';
import { lerConfiguracao } from '@/nucleo/auth/configuracao';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

import type { CicloDetalhe, EntradaDoCiclo, ListaDeCiclos } from './contrato';

/**
 * Camada de dados do ciclo de inscrição. Dado real, da API
 * (`bff/v1/admin/ciclos-inscricao`); toda regra — grade completa, um aberto por
 * vez, nada muda depois do encerramento — é conferida no banco (migração 0210).
 */
export const CAMINHO_DA_API = '/bff/v1/admin/ciclos-inscricao';

export const TAMANHO_DA_PAGINA = 20;

export const CHAVES = {
  lista: (pagina: number) => ['admin', 'ciclos-inscricao', 'lista', pagina] as const,
  detalhe: (id: string) => ['admin', 'ciclos-inscricao', 'detalhe', id] as const,
  tudo: ['admin', 'ciclos-inscricao'] as const,
};

export function useListaDeCiclos(pagina: number) {
  return useQuery({
    queryKey: CHAVES.lista(pagina),
    queryFn: () =>
      chamarApi<ListaDeCiclos>(`${CAMINHO_DA_API}?pagina=${pagina}&tamanho=${TAMANHO_DA_PAGINA}`),
    retry: naoInsistirEmRecusa,
  });
}

export function useDetalheDoCiclo(id: string) {
  return useQuery({
    queryKey: CHAVES.detalhe(id),
    queryFn: () => chamarApi<CicloDetalhe>(`${CAMINHO_DA_API}/${encodeURIComponent(id)}`),
    // Ciclo que não existe não passa a existir na segunda tentativa.
    retry: (tentativas, erro) =>
      !(erro instanceof ErroApi && erro.situacao === 404) && naoInsistirEmRecusa(tentativas, erro),
  });
}

/** Toda escrita devolve o detalhe atualizado; a lista é invalidada. */
function useEscrita<V>(executar: (v: V) => Promise<CicloDetalhe>) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: executar,
    onSuccess: (detalhe) => {
      cliente.setQueryData(CHAVES.detalhe(detalhe.ciclo.id), detalhe);
      void cliente.invalidateQueries({ queryKey: [...CHAVES.tudo, 'lista'] });
    },
  });
}

export function useCriarCiclo() {
  return useEscrita((v: EntradaDoCiclo & { chaveIdempotencia: string }) =>
    chamarApi<CicloDetalhe>(CAMINHO_DA_API, { metodo: 'POST', corpo: v }),
  );
}

export function useAlterarCiclo(id: string) {
  return useEscrita((v: EntradaDoCiclo & { versao: number; motivo?: string }) =>
    chamarApi<CicloDetalhe>(`${CAMINHO_DA_API}/${id}`, { metodo: 'PUT', corpo: v }),
  );
}

export function useEnviarEdital(id: string) {
  return useEscrita(
    (v: { versao: number; arquivoNome: string; conteudoBase64: string; motivo?: string }) =>
      chamarApi<CicloDetalhe>(`${CAMINHO_DA_API}/${id}/edital`, { metodo: 'PUT', corpo: v }),
  );
}

export function useAbrirCiclo(id: string) {
  return useEscrita((v: { versao: number }) =>
    chamarApi<CicloDetalhe>(`${CAMINHO_DA_API}/${id}/abrir`, { metodo: 'POST', corpo: v }),
  );
}

export function useCancelarCiclo(id: string) {
  return useEscrita((v: { versao: number; motivo: string }) =>
    chamarApi<CicloDetalhe>(`${CAMINHO_DA_API}/${id}/cancelar`, { metodo: 'POST', corpo: v }),
  );
}

/** Lê o arquivo escolhido como base64, sem o prefixo `data:`. */
export function lerComoBase64(arquivo: Blob): Promise<string> {
  return new Promise((resolver, rejeitar) => {
    const leitor = new FileReader();
    leitor.onload = () => {
      const texto = String(leitor.result ?? '');
      resolver(texto.slice(texto.indexOf(',') + 1));
    };
    leitor.onerror = () => rejeitar(leitor.error);
    leitor.readAsDataURL(arquivo);
  });
}

/**
 * Baixa o edital pela rota de administração. `chamarApi` só lê JSON, e o
 * arquivo precisa do mesmo cabeçalho de autorização.
 */
export async function baixarEdital(id: string, nome: string): Promise<void> {
  const sessao = sessaoAtiva();
  const resposta = await fetch(`${lerConfiguracao().api}${CAMINHO_DA_API}/${id}/edital`, {
    headers: sessao ? { Authorization: `Bearer ${sessao.acesso}` } : {},
    credentials: 'omit',
  });
  if (!resposta.ok) {
    throw new ErroApi(resposta.status, 'Não foi possível baixar o edital. Tente de novo.');
  }
  const url = URL.createObjectURL(await resposta.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = nome;
  link.click();
  URL.revokeObjectURL(url);
}

/** Mensagem da API quando ela disse o que fazer; senão, a genérica. */
export function mensagemDoErro(erro: unknown, generica: string): string {
  return erro instanceof ErroApi && erro.message ? erro.message : generica;
}
